import React, { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router'
import MainLayout from '@/layouts/MainLayout'
import Loading from '@/components/Loading'
import ScrollToTop from '@/components/ScrollToTop'
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import PageNotFound from './layouts/PageNotFound';
import { useStore } from './store'
import { useQueries, useQuery } from '@tanstack/react-query'
import { isInGame } from './lib/game'
import { getMeOptions } from './client/@tanstack/react-query.gen'
import { useParamsOptions } from './models/Config'
import {
  useCategoriesOptions,
  useGenresOptions,
  useRaresOptions,
  useGendersOptions
} from './models/Metadata'
import { useTagsOptions } from './models/Tag'
import { useWeaponsOptions } from './models/Weapon'
import { useCharacterRolesOptions, useCharacterHardsOptions } from './models/Character'
import { fetchAndApplyTranslation } from './models/Language'
import { getDefaultLanguage } from './lib/i18n'
import { useMediaQuery } from '@mantine/hooks'
import { showMessage } from './lib'
import { setSentryUser, clearSentryUser, setSentryContext } from '@/sentry-ready'

// Lazy load pages
const Home = React.lazy(() => import('@/pages/Home'))
const Outfits = React.lazy(() => import('@/pages/Outfits'))
const Guns = React.lazy(() => import('@/pages/Guns'))
const Collections = React.lazy(() => import('@/pages/Collections'))
const Collectibility = React.lazy(() => import('@/pages/Collectibity'))
const SkillCombos = React.lazy(() => import('@/pages/SkillCombos'))
const Characters = React.lazy(() => import('@/pages/Characters'))
// const Market = React.lazy(() => import('@/pages/Market'))
// const Tools = React.lazy(() => import('@/pages/Tools'))
// const Profile = React.lazy(() => import('@/pages/Profile'))

// Tool pages
// const Marketplace = React.lazy(() => import('@/pages/Marketplace'))
// const TacticalMap = React.lazy(() => import('@/pages/TacticalMap'))
// const CharacterCombo = React.lazy(() => import('@/pages/CharacterCombo'))
// const GunSkinList = React.lazy(() => import('@/pages/GunSkinList'))

function App() {

  const { userLoaded: _userLoaded, updateState, loginSuccess } = useStore()

  const config = useQueries({
    queries: [
      useParamsOptions(),
      useCategoriesOptions(),
      useGenresOptions(),
      useRaresOptions(),
      useGendersOptions(),
      useTagsOptions(),
      useWeaponsOptions(),
      useCharacterRolesOptions(),
      useCharacterHardsOptions(),
    ],
    combine: results => {
      return {
        params: results?.[0]?.data,
        categories: results?.[1]?.data,
        genres: results?.[2]?.data,
        rares: results?.[3]?.data,
        genders: results?.[4]?.data,
        tags: results?.[5]?.data,
        weapons: results?.[6]?.data,
        characterRoles: results?.[7]?.data,
        characterHards: results?.[8]?.data,
        isMetadataLoaded: results.slice(1, 9).every(r => r.isSuccess),
      }
    }
  })

  const { data: dataUser, status, error: _error } = useQuery({
    ...getMeOptions(),
    enabled: !!((isInGame() && loginSuccess) || !isInGame())
  })


  const isPC = useMediaQuery('(min-width: 1024px)');
  const isLandscape = useMediaQuery('(orientation: landscape)');

  useEffect(() => {
    if (status == 'success' || status == 'error') {
      updateState('userLoaded', true)
    }
  }, [status])

  useEffect(() => {
    if (dataUser) {
      setSentryUser({ id: dataUser.id, username: dataUser.name })
      setSentryContext('account', { uid: dataUser.uid })
    } else {
      clearSentryUser()
    }
  }, [dataUser])

  useEffect(() => {
    if (dataUser && dataUser?.uid === '162034602') {
      showMessage(sessionStorage.getItem('garena_token') || 'no token')
    }
  }, [dataUser])


  useEffect(() => {
    if (config?.params) {
      updateState('loaded', true)
      updateState('errors', config?.params?.errors)
    }
  }, [config?.params])

  useEffect(() => {
    updateState('isIngameBrowser', (isLandscape || isPC || (isInGame())))
  }, [isInGame(), isPC, isLandscape])

  // Bootstrap translations from API on mount
  useEffect(() => {
    fetchAndApplyTranslation(getDefaultLanguage()).catch((error) => {
      console.error('Failed to load initial translations:', error);
    });
  }, [])

  return (
    <BrowserRouter basename='/'>
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <ModalsProvider>
          <MainLayout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/item/:id" element={<Home />} />
              <Route path="/outfits" element={<Outfits />} />
              <Route path="/outfits/:id" element={<Outfits />} />
              <Route path="/guns" element={<Guns />} />
              <Route path="/guns/:id" element={<Guns />} />
              <Route path="/collections" element={<Collections />} />
              <Route path="/collections/:id" element={<Collections />} />
              <Route path="/collectibility" element={<Collectibility />} />
              <Route path="/collectibility/:category" element={<Collectibility />} />
              <Route path="/skill-combos" element={<SkillCombos />} />
              <Route path="/characters" element={<Characters />} />
              {/* <Route path="/market" element={<Market />} /> */}
              {/* <Route path="/tools" element={<Tools />} />
              <Route path="/tools/marketplace" element={<Marketplace />} />
              <Route path="/tools/tactical-map" element={<TacticalMap />} />
              <Route path="/tools/character-combos" element={<CharacterCombo />} />
              <Route path="/tools/gun-skins" element={<GunSkinList />} /> */}
              {/* <Route path="/profile" element={<Profile />} /> */}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </MainLayout>
        </ModalsProvider>
        <Notifications autoClose={3500} zIndex={1010} position='top-center' />
      </Suspense>
    </BrowserRouter>
  )
}

export default App